/**
 * AI CONTEXT
 * Purpose: one standout story bubble. The note is on top, the author and
 * date sit under it, and keyword tags sit to the right. The bubble is only
 * as wide as that content.
 * Used by: StudentStandoutMomentsTab, StudentStandoutMomentsList.
 */
import { Tag } from "antd"
import { type ReactNode } from "react"

import Flex from "#blocks/Flex"
import { COLORS } from "#styles/colors"
import { formatISOStringWithTime } from "#utils/dateHelpers"

const BYLINE_GAP_PX = 4

export type StandoutBubbleTag = {
  id: string
  text: string
  color?: string
}

const StandoutMomentBubble = ({
  note,
  authorName,
  createdAt,
  tags,
  extra,
  children,
  maxWidth = "100%",
}: {
  note?: string
  authorName: string
  createdAt: string
  tags: StandoutBubbleTag[]
  extra?: ReactNode
  children?: ReactNode
  maxWidth?: string
}) => (
  <div
    data-testid="profile-standout-bubble"
    style={{
      width: "fit-content",
      maxWidth,
      padding: "8px 12px",
      backgroundColor: COLORS.GRAY1,
      borderRadius: 12,
    }}
  >
    <Flex col gap={6}>
      <div
        style={{
          display: "flex",
          gap: 16,
          alignItems: "flex-start",
          justifyContent: "flex-start",
        }}
      >
        <div style={{ minWidth: 0 }}>
          <div data-testid="standout-note">{note}</div>
          <div
            data-testid="standout-author-byline"
            style={{
              marginTop: BYLINE_GAP_PX,
              fontSize: 12,
              color: COLORS.GRAY6,
              whiteSpace: "nowrap",
            }}
          >
            {authorName}
            <span style={{ marginLeft: 8 }}>
              {formatISOStringWithTime(createdAt)}
            </span>
          </div>
        </div>
        {tags.length > 0 ? (
          <div
            data-testid="standout-keyword-tags"
            style={{
              display: "flex",
              gap: 4,
              flex: "0 0 auto",
              flexWrap: "wrap",
            }}
          >
            {tags.map((tag) => (
              <Tag color={tag.color} key={tag.id}>
                {tag.text}
              </Tag>
            ))}
          </div>
        ) : null}
      </div>
      {extra}
      {children}
    </Flex>
  </div>
)

export default StandoutMomentBubble
